import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { useSelector } from "react-redux";
import FacebookCircularProgress from "src/Components/shared/shared/Loading";
import { getUrl } from "src/Utils/Common";
import useRefreshData from "src/Utils/CustomHooks/useRefreshData";
import { theme } from "../../../Theme/index";

const SKUShareChart = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [chartData, setChartData] = useState({
    categories: [],
    valueShare: [],
    skuShare: [],
    fairShareIndex: [],
  });

  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

  const projectId = useSelector((state) => state.userProject.projectId);

  const resetChartData = () => {
    setChartData({
      categories: [],
      valueShare: [],
      skuShare: [],
      fairShareIndex: [],
    });
  };

  const resetChartError = () => {
    setError("");
  };

  const resetChartLoading = () => {
    setLoading(false);
  };

  const { handleRefresh, isRefreshing } = useRefreshData(
    fetchSkusShare,
    [resetChartData, resetChartError, resetChartLoading]
  );

  async function fetchSkusShare() {
    setLoading(true);
    setError("");

      if (!projectId) {
        setError("Project ID not found.");
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${getUrl()}/strategy_creation/sku_share_value?project_id=${projectId}&date_filter=P12M&filters={}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
            },
          }
        );

        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

        const result = await response.json();

      setChartData({
        categories: result.brand || [],
        valueShare: Array.isArray(result.value_share) ? result.value_share : [],
        skuShare: Array.isArray(result.sku_share) ? result.sku_share : [],
        // keep raw; we'll scale/format safely in render
        fairShareIndex: Array.isArray(result.product_line_effeciency)
          ? result.product_line_effeciency
          : [],
      });
      setError("");
    } catch (err) {
      console.error("Error fetching SKU Share Value:", err);
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchSkusShare();
  }, [projectId]);

  // --- Helpers to keep axis & labels consistent ---
  const {
    fsiDataPercent,
    fsiAxisMax,
    fsiLabelFormatter,
  } = useMemo(() => {
    const raw = chartData.fairShareIndex.filter((v) => Number.isFinite(v));
    const maxRaw = raw.length ? Math.max(...raw) : 0;

    // If values look like ratios (<= 2.5), treat as 0–2 and display as %
    const isRatio = maxRaw > 0 && maxRaw <= 2.5;

    const percentData = isRatio ? raw.map((v) => v * 100) : raw;
    const axisMax =
      percentData.length ? Math.ceil(Math.max(...percentData) / 10) * 10 : 100;

    const formatter = (val) =>
      `${Number.isFinite(val) ? Math.round(val) : 0}%`;

    return {
      fsiDataPercent: percentData,
      fsiAxisMax: Math.max(axisMax, 100), // keep a sensible minimum
      fsiLabelFormatter: formatter,
    };
  }, [chartData.fairShareIndex]);

  // Ensure length alignment to avoid ApexCharts errors
  const safeCategories = chartData.categories || [];
  const pad = (arr) => (Array.isArray(arr) ? arr : []);
  const valueShare = pad(chartData.valueShare).slice(0, safeCategories.length);
  const skuShare = pad(chartData.skuShare).slice(0, safeCategories.length);
  const fsi = fsiDataPercent.slice(0, safeCategories.length);

  const series = [
    {
      name: "Value Share",
      type: "column",
      data: valueShare,
      yAxisIndex: 0, // Left axis
    },
    {
      name: "SKU Share",
      type: "column",
      data: skuShare,
      yAxisIndex: 0, // Left axis
    },
    {
      name: "Product Line Effeciency",
      type: "line",
      data: fsi,
      yAxisIndex: 1, // Right axis
    },
  ];

  const options = useMemo(() => ({
    chart: {
      type: "line",
      stacked: false,
      height: "100%",
      width: "100%",
      toolbar: {
        show: !isMobile,
        tools: {
          download: true,
          selection: false,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: false,
          reset: true,
        },
      },
    },
    xaxis: {
      categories: safeCategories,
      labels: {
        style: {
          fontSize: isMobile ? "10px" : "12px",
        },
        rotate: isMobile ? -45 : 0,
        rotateAlways: isMobile,
      },
    },
    yaxis: [
      {
        // Left axis for both bars
        min: 0,
        max: 100,
        title: { 
          text: "Value/SKU Share (%)",
          style: {
            fontSize: isMobile ? "10px" : "12px",
          },
        },
        labels: {
          formatter: (val) => `${Number.isFinite(val) ? Math.round(val) : 0}%`,
          style: {
            fontSize: isMobile ? "10px" : "12px",
          },
        },
      },
      {
        // Right axis for the line
        min: 0,
        max: fsiAxisMax, // dynamic based on data
        opposite: true,
        title: { 
          text: "Fair Share Index (%)",
          style: {
            fontSize: isMobile ? "10px" : "12px",
          },
        },
        labels: { 
          formatter: fsiLabelFormatter,
          style: {
            fontSize: isMobile ? "10px" : "12px",
          },
        },
      },
    ],
    dataLabels: {
      enabled: true,
      enabledOnSeries: [2], // only label the line
      formatter: function (val, { seriesIndex }) {
          if (seriesIndex === 2) {
            return `${(val * 100).toFixed(2)}%`; // multiply visible numbers by 100
          }
          return `${Math.round(val)}%`; // default for others if needed
        },
      style: {
        colors: ["#CD393B"],
        fontSize: isMobile ? "10px" : "12px",
        fontWeight: "bold",
      },
      offsetY: -10,
      background: { enabled: false },
    },
    stroke: {
      // Make the line visible
      width: [0, 0, 0],
    },
    markers: {
      // Safe mapping (if empty, ApexCharts is fine)
      discrete: fsi.map((_, idx) => ({
        seriesIndex: 2,
        dataPointIndex: idx,
        fillColor: "#CD393B",
        strokeColor: "#fff",
        size: isMobile ? 4 : 6,
      })),
    },
    colors: ["#08306B", "#53B5FF", "#CD393B"],
    grid: { 
      padding: { top: 30 },
      xaxis: {
        lines: {
          show: false,
        },
      },
    },
    tooltip: {
      shared: true,
      y: [
        {
          formatter: (val) =>
            `${Number.isFinite(val) ? Math.round(val) : 0}%`,
        },
        {
          formatter: (val) =>
            `${Number.isFinite(val) ? Math.round(val) : 0}%`,
        },
        {
          formatter: (val) => fsiLabelFormatter(val),
        },
      ],
    },
    plotOptions: {
      bar: { 
        columnWidth: isMobile ? "60%" : "45%",
      },
    },
    legend: { 
      show: true,
      position: "bottom",
      fontSize: isMobile ? "10px" : "12px",
      itemMargin: {
        horizontal: isMobile ? 5 : 10,
        vertical: isMobile ? 5 : 10,
      },
    },
  }), [isMobile, safeCategories, fsiAxisMax, fsiLabelFormatter, fsi]);

  return (
    <Box
      sx={{
        width: {
          xs: "110%",
          sm: "1000px",
        },
        height: {
          xs: "350px",
          sm: "400px",
        },
        margin: "auto",
        overflowX: "hidden",
        overflowY: "hidden",
      }}
    >
      <Typography
        sx={{
          fontWeight: "bold",
          fontSize: {
            xs: "0.9rem",
            sm: "1.2rem",
            md: "1.3rem",
          },
          px: {
            xs: 0.5,
            sm: 0.75,
            md: 1,
          },
          color: theme.palette.primary.main,
          textAlign: "center",
          m: 0,
          lineHeight: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 48,
        }}
      >
        SKU Share by Brand
      </Typography>

      {loading || isRefreshing ? (
        <Box
          sx={{
            p: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 300,
          }}
        >
          <FacebookCircularProgress />
        </Box>
      ) : error ? (
        <Box
          sx={{
            p: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 300,
          }}
        >
          <FacebookCircularProgress
            loading={false}
            hasData={false}
            onRefresh={handleRefresh}
            message={`${error} - Click to retry`}
          />
        </Box>
      ) : !chartData.categories || chartData.categories.length === 0 ? (
        <Box
          sx={{
            p: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 300,
          }}
        >
          <FacebookCircularProgress
            loading={false}
            hasData={false}
            onRefresh={handleRefresh}
            message="No data available - Click to retry"
          />
        </Box>
      ) : (
        <Chart
          options={options}
          series={series}
          type="line"
          width="100%"
          height={isMobile ? "80%" : "85%"}
        />
      )}
    </Box>
  );
};

export default SKUShareChart;
